/**
 * Who can use the portal, and what each of them is allowed to see.
 *
 * Two ways in exist side by side while the team moves over:
 *
 * - **Personal accounts.** Anyone can sign in with Google and gets an account
 *   with no role, which sees nothing but a "contact an admin" screen until an
 *   admin grants one. The role is read from the account on every request, so a
 *   change applies at once rather than whenever the session happens to expire.
 * - **Shared team passwords.** The original viewer / strategist / admin logins.
 *   Kept working until everyone has an account, then removed.
 *
 * Kept apart from `api.ts` because it is the one part of the contract every
 * future feature (assignments, reviews) will be gated on.
 */
import type { StrategistId } from "./api.js";

/**
 * Ordered from least to most access.
 *
 * - `user`: "results only" - the leaderboard, the top videos and the creative
 *   pages, and nothing of the creative pipeline (no briefs, no decks, no
 *   resources, no pipeline notifications). For e.g. a social media manager.
 * - `editor`: a user tied to one editor on the roster, who will get their own
 *   assignments.
 * - `strategist`: tied to one creative strategist; also sees the strategist
 *   payout.
 * - `admin`: everything, including settings and who has access.
 *
 * No role at all (`null`) is the state every new account starts in.
 */
export const ROLES = ["user", "editor", "strategist", "admin"] as const;

export type Role = (typeof ROLES)[number];

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && (ROLES as readonly string[]).includes(value);
}

/**
 * The role a shared-password session carried before roles were renamed.
 *
 * Sessions written before the rename still say `viewer`, and so do coach
 * conversations stored on the agent server, so the old name is read, never
 * written.
 */
export function roleFromLegacy(value: string | null | undefined): Role | null {
  if (value === "viewer") return "user";
  return isRole(value) ? value : null;
}

/** Any role at all unlocks the app; no role is the waiting room. */
export function hasAccess(role: Role | null | undefined): role is Role {
  return role != null;
}

export function isAdminRole(role: Role | null | undefined): boolean {
  return role === "admin";
}

/**
 * Whether this role works inside the creative pipeline: briefs, decks,
 * resources and the notifications they raise. Everyone but a results-only
 * `user`, and the API refuses the rest with 403 rather than an empty list.
 */
export function canUsePipeline(role: Role | null | undefined): boolean {
  return role === "editor" || role === "strategist" || role === "admin";
}

/**
 * Who the strategist payout is shown to: strategists, and the admins who agreed
 * the terms. Editors are not in that conversation.
 */
export function canSeeStrategistPayout(role: Role | null | undefined): boolean {
  return role === "strategist" || role === "admin";
}

/**
 * How much of the studio (the scriptwriter chats, ad jobs, publishing) a
 * session may use:
 *
 * - `full`: strategists and admins - everything the studio has.
 * - `script_chat`: a `user` or `editor` account an admin gave the "Script
 *   chat" add-on - their own scriptwriter chats, with files, voice and the
 *   images the scriptwriter makes, and nothing that creates pipeline work (no
 *   ad jobs, no briefs, no publishing, nobody else's chats).
 * - `null`: no studio at all.
 *
 * Read from the effective identity, so an admin viewing as an editor sees the
 * studio exactly as that editor's account would.
 */
export type StudioAccess = "full" | "script_chat";

/** Whether the "Script chat" add-on means anything for this role: strategists and admins have the whole studio already. */
export function canHaveScriptChat(role: Role | null | undefined): boolean {
  return role === "user" || role === "editor";
}

/** The studio this role and add-on open; see `StudioAccess`. */
export function studioAccess(role: Role | null | undefined, scriptChat: boolean | null | undefined): StudioAccess | null {
  if (role === "strategist" || role === "admin") return "full";
  if (scriptChat === true && canHaveScriptChat(role)) return "script_chat";
  return null;
}

/** How the current session was opened. */
export type AuthMethod = "google" | "password";

/** The roster entry an editor account is tied to. */
export interface LinkedEditor {
  id: number;
  name: string;
  color: string;
  /** The NAME their delivered file names start with (`CRIS`); null until an admin sets it. Absent where not read. */
  namingCode?: string | null;
}

/** The signed-in person, when the session belongs to a personal account. */
export interface SessionUser {
  id: number;
  email: string;
  /**
   * What the portal calls this person: the name an admin set, or the Google
   * profile name until one is set.
   */
  name: string;
  photoUrl: string | null;
  editor: LinkedEditor | null;
  strategist: StrategistId | null;
  /** The account's "Script chat" add-on (`studioAccess`); absent from older builds. */
  scriptChat?: boolean;
  createdAt: string;
}

export interface SessionInfo {
  authenticated: boolean;
  /**
   * Null when signed out, and when signed in to an account nobody has approved
   * yet. While an admin is viewing the platform as someone else this is the
   * persona's role, not theirs: every screen gates on it, and the point of the
   * exercise is that every screen sees what that person would.
   */
  role: Role | null;
  method: AuthMethod | null;
  /**
   * Null for shared-password sessions, which belong to nobody in particular.
   * While viewing as someone, the persona's account when one is linked to
   * them, so the greeting and the bell read exactly as theirs do.
   */
  user: SessionUser | null;
  /**
   * The role of whoever actually signed in. Differs from `role` only while an
   * admin is viewing as someone, and is what keeps the way back out on screen.
   */
  realRole: Role | null;
  /** Who the platform is being shown as, or null for the normal view. */
  viewAs: ViewAsInfo | null;
  /**
   * Every enabled account that is this same strategist, this one included, and
   * the strategists row they write as: one person may sign in with two Google
   * accounts, and a concept either of them owns is theirs. Absent from older
   * builds and from sessions that do not work as a strategist.
   */
  strategistAccountIds?: number[];
  strategistRowId?: number | null;
  /**
   * The separate apps this person may open, each reached by a full page load
   * from one sidebar entry. Granted per person, not per role, and never while
   * viewing as someone. Empty for nearly everyone; absent from older builds.
   */
  apps?: PlatformApp[];
  /**
   * Whether this person may create video ad jobs: always for a real admin, else the agents' "Strategists can
   * create video ad jobs" setting (`strategistVideoJobs`). Off, the brief card and the job form offer images
   * only. Absent from older servers: true.
   */
  videoJobs?: boolean;
  /**
   * How many signed-in accounts are waiting for an admin to give them a role
   * (no role, not disabled): the sidebar's badge beside "Users", without
   * downloading every account. Only for a session whose role is admin;
   * absent otherwise and from older servers.
   */
  pendingUsers?: number;
}

/** Icons a platform app entry may ask for; the sidebar maps each to one of its own. */
export const PLATFORM_APP_ICONS = ["briefcase", "app"] as const;

export type PlatformAppIcon = (typeof PLATFORM_APP_ICONS)[number];

/** One app the session can open, as the sidebar draws it. */
export interface PlatformApp {
  id: string;
  label: string;
  /** Same-origin path the entry links to with a full page load. */
  href: string;
  icon: PlatformAppIcon;
}

export const SIGNED_OUT: SessionInfo = {
  authenticated: false,
  role: null,
  method: null,
  user: null,
  realRole: null,
  viewAs: null,
  apps: [],
};

/* ---------------------------------------------------------------- view as */

/**
 * "View as": an admin seeing the whole platform as one editor, one strategist
 * or a plain user would, to check what a screen actually shows them.
 *
 * The persona is stored on the session row, server side, and every guard and
 * every query reads it — hiding a menu item in the browser would test nothing.
 * Only a session whose real role is admin can start one or keep one.
 */
export const VIEW_AS_ROLES = ["editor", "strategist", "user"] as const;

export type ViewAsRole = (typeof VIEW_AS_ROLES)[number];

/** Who the session is being shown as, as the client draws the banner. */
export interface ViewAsInfo {
  role: ViewAsRole;
  /** A name to put in the banner: the linked account's, else the roster's. Empty for a plain user. */
  label: string;
  editor: LinkedEditor | null;
  strategist: StrategistId | null;
  /** The account linked to that editor or strategist, whose bell and name are borrowed. */
  userId: number | null;
}

/** POST /api/auth/view-as */
export interface ViewAsRequest {
  role: ViewAsRole;
  editorId?: number;
  strategistId?: StrategistId;
}

/** One choice in the "Ver como…" dialog. */
export interface ViewAsEditorOption extends LinkedEditor {
  active: boolean;
  /** The personal account tied to this editor, if anyone has one. */
  account: { id: number; name: string } | null;
}

export interface ViewAsStrategistOption {
  id: StrategistId;
  name: string;
  account: { id: number; name: string } | null;
}

/** GET /api/auth/view-as/options */
export interface ViewAsOptions {
  editors: ViewAsEditorOption[];
  strategists: ViewAsStrategistOption[];
}

/**
 * The public half of the Firebase project, served at runtime so one image can
 * be deployed anywhere: Vite would otherwise bake it in at build time, and the
 * Docker build has no access to the environment.
 *
 * Null when Google sign-in is not configured on this instance, in which case
 * the login screen offers only the team password.
 */
export interface AuthConfig {
  google: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    appId: string | null;
  } | null;
}

/** POST /api/auth/google */
export interface GoogleSignInRequest {
  idToken: string;
}

/* -------------------------------------------------------- account management */

/** One personal account, as an admin sees it. */
export interface UserAccount {
  id: number;
  email: string;
  /** Name from the Google profile, which the person controls. */
  googleName: string | null;
  /** Name an admin gave them in the portal; null until one is set. */
  displayName: string | null;
  photoUrl: string | null;
  role: Role | null;
  editor: LinkedEditor | null;
  strategist: StrategistId | null;
  disabled: boolean;
  /** The "Script chat" add-on; only ever true for a `user` or `editor` account. */
  scriptChat: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  roleUpdatedAt: string | null;
}

/**
 * PATCH /api/admin/users/:id. Every field optional; omitted means unchanged.
 *
 * Editors and strategists must be tied to someone: an editor account without an
 * editor would have no assignments to show, so the link travels with the role.
 * Switching to a role that has no link clears the old one.
 */
export interface UserAccountPatch {
  role?: Role | null;
  /** Trimmed; an empty string clears it back to the Google name. */
  displayName?: string | null;
  editorId?: number | null;
  strategist?: StrategistId | null;
  disabled?: boolean;
  /**
   * The "Script chat" add-on. Only a `user` or `editor` account can have it;
   * a change to any other role clears it.
   */
  scriptChat?: boolean;
}
